/**
 * Reiter „Übersicht" (Bildschirm 1): Kennzahlen, Kunden × Signale, Geld bis
 * März, Anfragen-Heatmap, Vertriebstrichter und „Braucht dich".
 */

import { Fragment, type ReactNode } from 'react';
import { euro, zahl, PRIO_FARBE, ZUSTAND_ZEICHEN, type Uebersicht as UebersichtDaten, type Signal, type Vorgang, type Zustand } from '@/lib/leitwarte-api';
import { Karte, Kopf, Punkt, Swatch, Legende, Laden, Leer, Fehlerzeile, HinweisPille, Zeichen, T, prozentDelta, tagLabel, monatName, monatKurz, kurzDatum, isoTag, mehrzahl } from './bausteine';
import { Sparkline, GruppenBalken, Heatmap, Balkenzeile, RAMPE, rampFarbe } from './charts';

export type LeitwarteTab = 'uebersicht' | 'daten' | 'live' | 'vorgaenge' | 'zusagen';

interface Props {
  daten: UebersichtDaten | undefined;
  laedt: boolean;
  fehler: unknown;
  nochmal: () => void;
  oeffneTab: (tab: LeitwarteTab, id?: string) => void;
}

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const STUNDENBLOECKE = Array.from({ length: 12 }, (_, i) => String(i * 2));
const LEGENDE_ZUSTAENDE: Zustand[] = ['ok', 'beobachten', 'handeln', 'kritisch', 'bau'];

/** Knopftext in „Braucht dich" nach Art des vorbereiteten Artefakts. */
export function knopfText(v: Vorgang): { text: string; primaer: boolean } {
  const art = v.artefakt?.art;
  if (art === 'mail_entwurf') return { text: 'Entwurf', primaer: true };
  if (art === 'aenderung') return { text: 'Ansehen', primaer: true };
  if (art === 'leitfaden') return { text: 'Leitfaden', primaer: false };
  if (v.typ === 'zusage' || v.typ === 'termin') return { text: 'Planen', primaer: false };
  return { text: 'Öffnen', primaer: false };
}

function Kachel({ label, wert, fuss, titel }: { label: string; wert: ReactNode; fuss?: ReactNode; titel?: string }) {
  return (
    <Karte style={{ padding: '16px 18px', gap: 4 }}>
      <span className="lw-12 lw-sek">{label}</span>
      <span style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1 }} title={titel}>{wert}</span>
      {fuss ? <span className="lw-12 lw-sek" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>{fuss}</span> : null}
    </Karte>
  );
}

function SignalZelle({ s }: { s: Signal | undefined }) {
  if (!s || s.zustand === 'keine') return <span className="lw-ged" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s?.text || 'nicht gebucht'}</span>;
  const wort = ZUSTAND_ZEICHEN[s.zustand]?.wort ?? '';
  return (
    <span className="lw-sek" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={`${wort}${s.text ? `: ${s.text}` : ''}`}>
      <Zeichen zustand={s.zustand} /> {s.text || wort}
    </span>
  );
}

export default function Uebersicht({ daten, laedt, fehler, nochmal, oeffneTab }: Props) {
  if (!daten) return laedt ? <Laden text="Lade die Übersicht …" /> : <Fehlerzeile error={fehler} retry={nochmal} />;

  const kpis = daten.kpis;
  const kunden = daten.kunden ?? [];
  const monate = daten.geld?.monate ?? [];
  const hinweise = daten.geld?.hinweise ?? [];
  const hm = daten.anfragen_heatmap;
  const matrix = hm?.matrix ?? [];
  const vertrieb = daten.vertrieb;
  const vorgaenge = daten.vorgaenge ?? [];
  const zusagen = daten.zusagen ?? [];

  // Kachel 1: MRR und Plan
  const plan = kpis.mrr_plan ?? [];
  const planLetzter = plan[plan.length - 1];

  // Kachel 2: Konto (Stand kann älter als heute sein)
  const kontoDatum = kpis.konto_datum ? new Date(kpis.konto_datum) : null;
  const kontoLabel = kontoDatum && !Number.isNaN(kontoDatum.getTime()) && isoTag(kontoDatum) !== isoTag(new Date()) ? `Konto, Stand ${kurzDatum(kpis.konto_datum)}` : 'Konto heute';

  // Kachel 3: Anfragen
  const delta = prozentDelta(kpis.anfragen30, kpis.anfragen_davor30);

  // Kachel 4: Publivo
  const pub = kpis.publivo;
  const bald = (pub.naechste ?? []).slice(0, Math.max(0, pub.trial_enden_7t));
  const baldLabels = Array.from(new Set(bald.map((n) => tagLabel(n.endet)).filter(Boolean)));
  const wann = baldLabels.length === 1 ? baldLabels[0] : baldLabels.length > 1 ? `bis ${baldLabels[baldLabels.length - 1]}` : 'in 7 Tagen';
  let trialsText = `${zahl(pub.trial)} ${mehrzahl(pub.trial, 'Trial', 'Trials')}`;
  if (pub.trial_enden_7t > 0) trialsText += `, ${zahl(pub.trial_enden_7t)} ${mehrzahl(pub.trial_enden_7t, 'endet', 'enden')} ${wann}`;
  if (pub.abgelaufen > 0) trialsText += ` · ${zahl(pub.abgelaufen)} abgelaufen`;

  // Kachel 5: Braucht dich
  const vg = kpis.vorgaenge;

  // Matrix
  const maxAnfragen = Math.max(0, ...kunden.map((k) => k.anfragen30));

  // Braucht dich: die ersten vier offenen Vorgänge
  const offene = vorgaenge.filter((v) => v.status === 'offen');
  const liste = (offene.length ? offene : vorgaenge).slice(0, 4);
  const anzahlOffen = vg?.offen ?? offene.length;

  const bisMonat = monate.length ? monatName(monate[monate.length - 1].monat) : 'März';

  return (
    <>
      {/* Kennzahlen */}
      <div className="lw-grid lw-g-kpi">
        <Karte style={{ gap: 6 }}>
          <span className="lw-12 lw-sek">Feste Einnahmen im {kpis.mrr_monat_label ?? 'Monat'}, ohne Projektrechnungen</span>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 52, fontWeight: 800, lineHeight: 1 }}>{euro(kpis.mrr_euro)}</span>
            {plan.length > 1 ? (
              <Sparkline werte={plan.map((p) => p.euro)} breite={170} hoehe={44} style={{ marginBottom: 4 }} label={`Plan bis ${monatName(planLetzter.monat)}: ${euro(planLetzter.euro)}`} />
            ) : null}
          </div>
          {kpis.mrr_teile && kpis.mrr_teile.length ? (
            <span className="lw-12 lw-sek">
              {kpis.mrr_teile.map((t, i) => (
                <span key={t.label}>{i ? ' · ' : ''}{zahl(t.anzahl)} {t.label} <span className="lw-weiss lw-fett">{euro(t.euro)}</span></span>
              ))}
            </span>
          ) : null}
          <span className="lw-12 lw-sek">
            {planLetzter ? (
              <>
                Plan bis {monatName(planLetzter.monat)}: <span className="lw-weiss lw-fett">{euro(planLetzter.euro)}</span>
                {kpis.mrr_plan_hinweis ? `, ${kpis.mrr_plan_hinweis}` : ''}
              </>
            ) : (
              kpis.mrr_plan_hinweis ?? 'Noch kein Plan hinterlegt'
            )}
          </span>
          {kpis.einnahmen_naechster_monat_euro ? (
            <span className="lw-12 lw-sek">Alle Einnahmen im {kpis.naechster_monat_label} laut Plan, mit Projekten: <span className="lw-weiss lw-fett">{euro(kpis.einnahmen_naechster_monat_euro)}</span></span>
          ) : null}
          {kpis.mrr_live_hinweis ? <span className="lw-11 lw-muted">{kpis.mrr_live_hinweis}</span> : null}
        </Karte>

        <Kachel label={kontoLabel} wert={euro(kpis.konto_euro)} fuss={<>davon USt-Topf {euro(kpis.ust_topf_euro)}</>} />

        <Kachel
          label="Anfragen 30 Tage"
          wert={zahl(kpis.anfragen30)}
          fuss={delta ? <><span className="lw-weiss lw-fett">{delta}</span> gegenüber den 30 Tagen davor</> : <>{zahl(kpis.anfragen_davor30)} in den 30 Tagen davor</>}
        />

        <Kachel
          label="Publivo"
          wert={<>{zahl(pub.zahlend)} <span style={{ fontSize: 14, fontWeight: 600, color: T.sek }}>zahlend</span></>}
          fuss={trialsText}
          titel={(pub.naechste ?? []).map((n) => `${n.name}: ${kurzDatum(n.endet)}`).join(', ') || undefined}
        />

        <Kachel
          label="Braucht dich"
          wert={zahl(vg.offen)}
          fuss={
            <>
              <Punkt farbe={T.krit} />{zahl(vg.rot)} dringend
              {vg.gelb > 0 ? <> · <Punkt farbe={T.warn} />{zahl(vg.gelb)} wichtig</> : null}
              {' · '}{zahl(vg.vorbereitet)} vorbereitet
            </>
          }
        />
      </div>

      {/* Kunden × Signale und Geld */}
      <div className="lw-grid lw-g-32">
        <Karte style={{ gap: 10 }}>
          <Kopf titel="Kunden × Signale" sub="Anfragen in 30 Tagen, blau nach Menge · Zustände mit Zeichen und Wort" />
          <div className="lw-scroll-x">
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 200px) 90px repeat(4, minmax(110px, 1fr)) 90px', gap: '2px 10px', fontSize: 12, alignItems: 'center', minWidth: 720 }}>
              <span className="lw-ged" style={{ padding: '4px 0' }}>Kunde</span>
              <span className="lw-ged" style={{ textAlign: 'center' }}>Anfragen</span>
              <span className="lw-ged">Google Ads</span>
              <span className="lw-ged">Website</span>
              <span className="lw-ged">Publivo</span>
              <span className="lw-ged">Zahlung</span>
              <span className="lw-ged" style={{ textAlign: 'right' }}>Kontakt</span>
              {kunden.map((k) => (
                <Fragment key={k.id}>
                  <span style={{ fontWeight: 600, padding: '6px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={k.name}>{k.name}</span>
                  <span
                    title={`${zahl(k.anfragen30)} Anfragen in 30 Tagen, davor ${zahl(k.anfragen_davor30)}`}
                    style={{ background: rampFarbe(k.anfragen30, maxAnfragen, 7), color: k.anfragen30 > 0 ? T.text : T.sek, borderRadius: 6, textAlign: 'center', padding: '7px 0', fontWeight: 700 }}
                  >
                    {zahl(k.anfragen30)}
                  </span>
                  <SignalZelle s={k.ads} />
                  <SignalZelle s={k.website} />
                  <SignalZelle s={k.publivo} />
                  <SignalZelle s={k.zahlung} />
                  <span className="lw-sek" style={{ textAlign: 'right', whiteSpace: 'nowrap' }} title={k.kontakt?.tage != null ? `letzter Kontakt vor ${k.kontakt.tage} Tagen` : undefined}>{k.kontakt?.text ?? ''}</span>
                </Fragment>
              ))}
            </div>
          </div>
          {kunden.length === 0 ? <Leer text="Noch keine Kunden im Lauf." /> : null}
          <div className="lw-trenner" style={{ marginTop: 'auto', display: 'flex', gap: 16, fontSize: 11, color: T.ged, paddingTop: 6, flexWrap: 'wrap' }}>
            {LEGENDE_ZUSTAENDE.map((z) => (
              <span key={z}><Zeichen zustand={z} /> {ZUSTAND_ZEICHEN[z].wort}</span>
            ))}
          </div>
        </Karte>

        <Karte>
          <Kopf titel={`Geld bis ${bisMonat}`} sub="laut deinem Finanzplan, enthält Zielkunden" />
          <Legende eintraege={[{ farbe: T.blau, label: 'Einnahmen' }, { farbe: T.orange, label: 'Kosten und Steuerrücklage' }, { farbe: T.aqua, label: 'Kontostand', art: 'linie' }]} />
          {monate.length ? (
            <GruppenBalken
              gruppen={monate.map((m) => ({
                label: m.label || monatKurz(m.monat),
                werte: [m.einnahmen, m.ausgaben],
                linie: m.kontostand,
                titel: `${m.label || monatKurz(m.monat)}: Einnahmen ${euro(m.einnahmen)}, Kosten ${euro(m.ausgaben)}, Kontostand ${euro(m.kontostand)}, frei ${euro(m.frei)}${m.ust_zahlung ? `, USt ${euro(m.ust_zahlung)}` : ''}${m.geplant ? ' (mit geplanten Einmalbeträgen)' : ''}`,
              }))}
              format={(v) => euro(v)}
              label={`Monatliche Einnahmen, Kosten und Kontostand bis ${bisMonat}`}
            />
          ) : (
            <Leer text="Kein Finanzplan hinterlegt." />
          )}
          {hinweise.length ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: 'auto' }}>
              {hinweise.map((h, i) => <HinweisPille key={i} text={h} />)}
            </div>
          ) : null}
        </Karte>
      </div>

      {/* Heatmap, Vertrieb, Braucht dich */}
      <div className="lw-grid lw-g-drei">
        <Karte>
          <Kopf titel="Wann Anfragen kommen" sub={hm ? `${zahl(hm.gesamt)} Anfragen, ${zahl(hm.tage)} Tage, je 2 Stunden` : undefined} />
          {matrix.length ? (
            <Heatmap
              zeilen={WOCHENTAGE.map((t, r) => ({ label: t, werte: matrix[r] ?? [] }))}
              spalten={STUNDENBLOECKE}
              farbe={(v) => rampFarbe(v ?? 0, hm?.max ?? 0, 8)}
              titel={(r, c, v) => `${WOCHENTAGE[r]} ${c * 2} bis ${c * 2 + 2} Uhr: ${zahl(v ?? 0)} ${mehrzahl(v ?? 0, 'Anfrage', 'Anfragen')}`}
            />
          ) : (
            <Leer text="Noch keine Anfragen im Zeitraum." />
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: T.ged, marginTop: 'auto', flexWrap: 'wrap' }}>
            <span>0</span>
            {[0, 1, 3, 5, 8].map((i) => <Swatch key={i} farbe={RAMPE[i]} />)}
            <span>{zahl(hm?.max ?? 0)} {mehrzahl(hm?.max ?? 0, 'Anfrage', 'Anfragen')}</span>
            {hm?.hinweis ? <span style={{ marginLeft: 'auto', color: T.sek }}>{hm.hinweis}</span> : null}
          </div>
        </Karte>

        <Karte style={{ gap: 10 }}>
          <Kopf titel="Vertrieb" sub="Betriebe im Ablauf" />
          {vertrieb ? (
            <>
              <Balkenzeile
                zeilen={[
                  { label: 'Kandidaten', wert: vertrieb.kandidaten, farbe: RAMPE[3] },
                  { label: 'Brief raus', wert: vertrieb.briefe, farbe: RAMPE[4] },
                  { label: 'Angerufen', wert: vertrieb.angerufen, farbe: RAMPE[5] },
                  { label: 'Testphase', wert: vertrieb.testphase, farbe: RAMPE[7] },
                  { label: 'Kunde', wert: vertrieb.kunde, farbe: RAMPE[8] },
                ]}
                max={Math.max(1, vertrieb.kandidaten)}
                hoehe={18}
                luecke={10}
                abstand={8}
              />
              <div className="lw-innen" style={{ marginTop: 'auto', padding: '10px 12px', fontSize: 12, color: T.sek, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span className="lw-weiss lw-fett">{zahl(vertrieb.briefe)} {mehrzahl(vertrieb.briefe, 'Brief', 'Briefe')}, {zahl(vertrieb.angerufen)} {mehrzahl(vertrieb.angerufen, 'Anruf', 'Anrufe')}.</span>
                <span>
                  {vertrieb.hinweis
                    ?? (vertrieb.anrufe_faellig > 0
                      ? `${zahl(vertrieb.anrufe_faellig)} ${mehrzahl(vertrieb.anrufe_faellig, 'Anruf ist', 'Anrufe sind')} fällig. Der Nachtlauf legt dir die Anrufliste mit Gesprächsöffner in die Vorgänge.`
                      : 'Kein Anruf fällig.')}
                </span>
              </div>
            </>
          ) : (
            <Leer text="Keine Vertriebsdaten." />
          )}
        </Karte>

        <Karte>
          <Kopf
            titel="Braucht dich"
            rechts={
              <a href="?tab=vorgaenge" style={{ fontSize: 12, fontWeight: 600 }} onClick={(e) => { e.preventDefault(); oeffneTab('vorgaenge'); }}>
                Alle {zahl(anzahlOffen)} durchgehen
              </a>
            }
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
            {liste.map((v) => {
              const k = knopfText(v);
              return (
                <div key={v.id} className="lw-innen lw-zeile">
                  <Punkt farbe={PRIO_FARBE[v.prioritaet] ?? T.blau} />
                  <span style={{ flexGrow: 1, color: T.text, fontWeight: 600, minWidth: 0 }} title={v.beschreibung ?? undefined}>{v.titel}</span>
                  <button type="button" className={`lw-knopf lw-knopf-klein${k.primaer ? ' lw-knopf-primaer' : ''}`} onClick={() => oeffneTab('vorgaenge', v.id)}>{k.text}</button>
                </div>
              );
            })}
            {liste.length === 0 ? <Leer text="Nichts offen. Der Nachtlauf hat nichts gefunden, was dich braucht." /> : null}
          </div>
          {zusagen.length ? (
            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: T.sek, paddingTop: 6 }}>
              <span className="lw-ged lw-11" style={{ fontWeight: 600 }}>Zusagen</span>
              {zusagen.slice(0, 3).map((z) => (
                <button key={z.id} type="button" className="lw-zeile" style={{ gap: 8, background: 'none', border: 0, padding: 0, color: T.sek, font: 'inherit', textAlign: 'left' }} onClick={() => oeffneTab('zusagen')}>
                  <span className="lw-kasten" style={{ width: 12, height: 12, borderRadius: 3 }} />
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {z.titel}
                    {z.kunde_name ? <span className="lw-ged"> · {z.kunde_name}</span> : null}
                  </span>
                  {z.faellig_am ? <span className="lw-ged" style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>bis {kurzDatum(z.faellig_am)}</span> : null}
                </button>
              ))}
            </div>
          ) : null}
        </Karte>
      </div>
    </>
  );
}
